import {useCallback} from 'react'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import type {QueryClient} from '@tanstack/react-query'
import {agendaExternaService} from '../services/AgendaExternaService'
import {agendaRecebimentoService} from '../services/AgendaRecebimentoService'
import {tributumService} from '../services/TributumService'
import {agendaExternaKeys, agendaRecebimentoKeys} from './query-keys'
import type {
  AgendaExternaCadastroRequest,
  AgendaRecebimentoAcceptNewBookingDateRequest,
  AgendaRecebimentoRejectNewBookingDateRequest,
  Identificador,
  XmlNfeResponse,
} from '../types'

function filialValida(filial: Identificador | undefined): filial is Identificador {
  return filial !== undefined && filial !== null && filial !== ''
}

function invalidarListasEVolumesDeCadastro(queryClient: QueryClient, filialEntrega?: Identificador) {
  void queryClient.invalidateQueries({queryKey: [...agendaRecebimentoKeys.all, 'agendamentos']})

  if (filialValida(filialEntrega)) {
    // A Home grava esse cache sempre com a filial em string (stores/filial-store.ts); normaliza aqui
    // para a chave de invalidação bater com a chave de fato usada, independente do tipo recebido.
    const filial = String(filialEntrega)
    void queryClient.invalidateQueries({queryKey: agendaExternaKeys.volumes(filial)})
    void queryClient.invalidateQueries({queryKey: agendaExternaKeys.volumeGeral(filial)})
  }
}

function invalidarListasEDetalhesDaAgenda(queryClient: QueryClient, agendaId: Identificador) {
  void queryClient.invalidateQueries({queryKey: [...agendaRecebimentoKeys.all, 'agendamentos']})
  void queryClient.invalidateQueries({queryKey: agendaRecebimentoKeys.detalhes(agendaId)})
  void queryClient.invalidateQueries({queryKey: agendaRecebimentoKeys.resumo(agendaId)})
}

export function useCadastrarAgendamentoMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    retry: false,
    mutationFn: (dto: AgendaExternaCadastroRequest) => agendaExternaService.finalizeScheduling(dto),
    onSuccess: (_data, variables) => {
      invalidarListasEVolumesDeCadastro(queryClient, variables.filialEntrega)
    },
  })
}

export function usePreCadastrarAgendamentoMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    retry: false,
    mutationFn: (dto: AgendaExternaCadastroRequest) => agendaExternaService.finalizePreScheduling(dto),
    onSuccess: (_data, variables) => {
      invalidarListasEVolumesDeCadastro(queryClient, variables.filialEntrega)
    },
  })
}

export function useConcluirPreAgendamentoMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    retry: false,
    mutationFn: (dto: AgendaExternaCadastroRequest) => agendaExternaService.completePreScheduling(dto),
    onSuccess: (_data, variables) => {
      invalidarListasEVolumesDeCadastro(queryClient, variables.filialEntrega)
    },
  })
}

export function useAceitarNovaDataAgendamentoMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    retry: false,
    mutationFn: (dto: AgendaRecebimentoAcceptNewBookingDateRequest) => agendaRecebimentoService.acceptNewSchedulingDate(dto),
    onSuccess: (_data, variables) => {
      invalidarListasEDetalhesDaAgenda(queryClient, variables.agendaId)
    },
  })
}

export function useRejeitarNovaDataAgendamentoMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    retry: false,
    mutationFn: (dto: AgendaRecebimentoRejectNewBookingDateRequest) => agendaRecebimentoService.rejectNewScheduleDate(dto),
    onSuccess: (_data, variables) => {
      invalidarListasEDetalhesDaAgenda(queryClient, variables.agendaId)
    },
  })
}

export function useBuscarXmlNfeAgendaExterna() {
  const queryClient = useQueryClient()

  return useCallback(
    (chaveAcesso: string) =>
      queryClient.fetchQuery<XmlNfeResponse>({
        queryKey: agendaExternaKeys.nfe('agenda', chaveAcesso),
        queryFn: async () => {
          const {data} = await agendaExternaService.getXmlNfe(chaveAcesso)
          return data
        },
        staleTime: 0,
      }),
    [queryClient],
  )
}

export function useBuscarNfeTributum() {
  const queryClient = useQueryClient()

  return useCallback(
    (chaveAcesso: string) =>
      queryClient.fetchQuery<XmlNfeResponse>({
        queryKey: agendaExternaKeys.nfe('tributum', chaveAcesso),
        queryFn: async () => {
          const {data} = await tributumService.getNfe(chaveAcesso)
          return data
        },
        staleTime: 0,
      }),
    [queryClient],
  )
}
